import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc, updateDoc, arrayUnion } from "firebase/firestore";
import { db } from "../../../Firebase";
import { V3Room, V3Event } from "../types/v3types";
import { useAuth } from "../hooks/useAuth";
import styled from "styled-components";

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
`;

const JoinCard = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  padding: 40px;
  width: 100%;
  max-width: 500px;
`;

const Title = styled.h1`
  font-size: 28px;
  margin-bottom: 10px;
  text-align: center;
  color: #333;
`;

const RoomInfo = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 30px;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const Label = styled.span`
  font-weight: 600;
  color: #666;
`;

const Value = styled.span`
  color: #333;
`;

const StatusBadge = styled.span<{ status: string }>`
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  background-color: ${props => {
    switch (props.status) {
      case "waiting": return "#ffc107";
      case "running": return "#28a745";
      case "finished": return "#6c757d";
      default: return "#6c757d";
    }
  }};
  color: white;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const InputLabel = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #555;
`;

const Input = styled.input`
  padding: 12px 16px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 16px;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const Button = styled.button`
  padding: 14px 28px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
  }
`;

const ErrorMessage = styled.div`
  background: #ffe3e3;
  color: #c92a2a;
  padding: 12px;
  border-radius: 8px;
  font-size: 14px;
  text-align: center;
`;

const LoadingMessage = styled.div`
  text-align: center;
  font-size: 18px;
  color: #666;
`;

const V3RoomJoin: React.FC = () => {
  const { projectId, roomId } = useParams<{ projectId: string; roomId: string }>();
  const navigate = useNavigate();
  const { user: firebaseUser, loading: authLoading } = useAuth();
  const [room, setRoom] = useState<V3Room | null>(null);
  const [event, setEvent] = useState<V3Event | null>(null);
  const [accessCode, setAccessCode] = useState("");
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!projectId || !roomId) {
      navigate("/");
      return;
    }

    if (authLoading) return;

    if (!firebaseUser) {
      navigate(`/v3/${projectId}/start`);
      return;
    }

    const loadRoomData = async () => {
      try {
        const roomDoc = await getDoc(doc(db, "v3rooms", roomId));
        if (roomDoc.exists()) {
          const roomData = roomDoc.data() as V3Room;
          setRoom(roomData);

          // Check if already joined
          const userId = firebaseUser.uid + "_" + projectId;
          if (roomData.participants?.includes(userId)) {
            navigate(`/v3/${projectId}/room/${roomId}/game`);
            return;
          }

          // Load event data
          const eventDoc = await getDoc(doc(db, "v3events", roomData.eventId));
          if (eventDoc.exists()) {
            setEvent(eventDoc.data() as V3Event);
          }
        } else {
          setError("ルームが見つかりません");
        }
      } catch (err) {
        setError("ルームの読み込みに失敗しました");
      } finally {
        setLoading(false);
      }
    };

    loadRoomData();
  }, [projectId, roomId, navigate, firebaseUser, authLoading]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!room || !projectId || !roomId) return;
    
    setError("");
    setJoining(true);

    try {
      // Check access code if needed
      if (!room.isPublic && room.accessCode && room.accessCode !== accessCode) {
        throw new Error("アクセスコードが正しくありません");
      }

      // Check room status
      if (room.status === "finished") {
        throw new Error("このルームは既に終了しています");
      }

      if (room.status === "running" && !room.settings?.allowLateJoin) {
        throw new Error("このルームは途中参加ができません");
      }

      // Check max participants
      if (room.settings?.maxParticipants && 
          room.participants.length >= room.settings.maxParticipants) {
        throw new Error("このルームは満員です");
      }

      if (!firebaseUser) {
        navigate(`/v3/${projectId}/start`);
        return;
      }

      const userId = firebaseUser.uid + "_" + projectId;

      // Join room
      await updateDoc(doc(db, "v3rooms", roomId), {
        participants: arrayUnion(userId)
      });

      navigate(`/v3/${projectId}/room/${roomId}/game`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "参加に失敗しました");
      setJoining(false);
    }
  };

  if (loading || authLoading) {
    return (
      <Container>
        <JoinCard>
          <LoadingMessage>読み込み中...</LoadingMessage>
        </JoinCard>
      </Container>
    );
  }

  if (!room || !event) {
    return (
      <Container>
        <JoinCard>
          <ErrorMessage>{error || "ルームが見つかりません"}</ErrorMessage>
          <Button 
            onClick={() => navigate(`/v3/${projectId}/mypage`)}
            style={{ marginTop: "20px", width: "100%" }}
          >
            マイページに戻る
          </Button>
        </JoinCard>
      </Container>
    );
  }

  return (
    <Container>
      <JoinCard>
        <Title>ルームに参加</Title>
        
        <RoomInfo>
          <InfoRow>
            <Label>ルーム名:</Label>
            <Value>{room.roomName}</Value>
          </InfoRow>
          <InfoRow>
            <Label>イベント:</Label>
            <Value>{event.eventName}</Value>
          </InfoRow>
          <InfoRow>
            <Label>ステータス:</Label>
            <StatusBadge status={room.status}>
              {room.status === "waiting" && "待機中"}
              {room.status === "running" && "開催中"}
              {room.status === "finished" && "終了"}
            </StatusBadge>
          </InfoRow>
          <InfoRow>
            <Label>参加者数:</Label>
            <Value>
              {room.participants?.length || 0}人
              {room.settings?.maxParticipants && ` / ${room.settings.maxParticipants}人`}
            </Value>
          </InfoRow>
        </RoomInfo>

        <Form onSubmit={handleSubmit}>
          {!room.isPublic && (
            <InputGroup>
              <InputLabel htmlFor="accessCode">アクセスコード</InputLabel>
              <Input
                id="accessCode"
                type="text"
                value={accessCode}
                onChange={(e) => setAccessCode(e.target.value)}
                placeholder="6文字のコードを入力"
                required
                disabled={joining}
                autoComplete="off"
              />
            </InputGroup>
          )}
          
          {error && <ErrorMessage>{error}</ErrorMessage>}
          
          <Button 
            type="submit" 
            disabled={joining || room.status === "finished"}
          >
            {joining ? "参加中..." : "参加する"}
          </Button>
        </Form>
      </JoinCard>
    </Container>
  );
};

export default V3RoomJoin;